import { Show, createEffect, createMemo, createSignal, onCleanup, onMount, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { PauseIcon } from '../icons-library/pause';
import { PlayIcon } from '../icons-library/play';
import { CrossfadeImage } from './crossfade-image';
import { ProgressArc } from './progress-arc';
import { HeroCviSlideData } from './hero-cvi-types';
import {
    LARGER_IMAGE_SIZES,
    LARGER_IMAGE_WIDTHS,
    SMALLER_IMAGE_SIZES,
    SMALLER_IMAGE_WIDTHS,
} from './image-helpers';

const AUTO_ADVANCE_MS = 6000;
/** Must match `--animate-cvi-hero-slide-fade-{in,out}` durations in tailwind-theme-misc.css. */
const CROSSFADE_MS = 600;

const slideIsRenderable = (slide?: HeroCviSlideData) =>
    !!slide && (!!slide.largerImage?.url || !!slide.smallerImage?.url || !!slide.captionName);

type HeroCviCarouselProps = {
    slides?: HeroCviSlideData[];
};

/**
 * Carousel variant — auto-advancing paired-image slideshow with crossfade between slides.
 * Pauses on hover, on the play/pause toggle, and under prefers-reduced-motion. If
 * `slides.length < 2`, renders the single visible slide statically (no controls).
 */
export const HeroCviCarousel = (props: HeroCviCarouselProps) => {
    const { localize } = useContext(AppContext);

    const slides = createMemo(() => (props.slides ?? []).filter(slideIsRenderable));
    const isInteractive = createMemo(() => slides().length >= 2);

    const [currentIndex, setCurrentIndex] = createSignal(0);
    // User intent vs transient hover are tracked separately so a manual pause survives a hover-out.
    const [userPaused, setUserPaused] = createSignal(false);
    const [isHovered, setIsHovered] = createSignal(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = createSignal(false);
    const isPlaying = createMemo(() => !userPaused() && !isHovered() && !prefersReducedMotion());

    const currentSlide = createMemo(() => slides()[currentIndex()]);

    // Track the previous slide for the duration of the crossfade so its images can stay rendered
    // (layered underneath the new ones) while they fade out. Cleared once the animation completes.
    const [previousSlide, setPreviousSlide] = createSignal<HeroCviSlideData | undefined>();
    createEffect<HeroCviSlideData | undefined>((prev) => {
        const curr = currentSlide();
        if (prev && prev !== curr) {
            setPreviousSlide(prev);
            const timeout = setTimeout(() => setPreviousSlide(undefined), CROSSFADE_MS);
            onCleanup(() => clearTimeout(timeout));
        }
        return curr;
    });

    let intervalId: ReturnType<typeof setInterval> | undefined;
    const clearAutoAdvance = () => {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = undefined;
        }
    };
    const startAutoAdvance = () => {
        clearAutoAdvance();
        if (!isInteractive() || !isPlaying()) return;
        intervalId = setInterval(() => {
            setCurrentIndex((i) => (i + 1) % slides().length);
        }, AUTO_ADVANCE_MS);
    };

    onMount(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            setPrefersReducedMotion(mq.matches);
            const listener = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
            mq.addEventListener('change', listener);
            onCleanup(() => mq.removeEventListener('change', listener));
        }
    });
    // Single source of truth: react to isPlaying changes (driven by userPaused / isHovered /
    // prefersReducedMotion / isInteractive) and start or clear the interval accordingly.
    createEffect(() => {
        if (isPlaying()) startAutoAdvance();
        else clearAutoAdvance();
    });
    onCleanup(clearAutoAdvance);

    // mouseenter/mouseleave (not over/out) so child-boundary crossings inside the region don't toggle.
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    const togglePlay = () => setUserPaused((p) => !p);

    return (
        <div
            class="grid grid-cols-12 gap-x-[var(--spacing-gap-mobile)] tablet-original:flex tablet-original:justify-end tablet-original:items-end tablet-original:gap-x-cvi-6"
            role="region"
            aria-roledescription={localize('carousel', 'carousel')}
            aria-label={localize('hero-carousel', 'Hero carousel')}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Larger image — 7 of 12 cols on mobile; from tablet-original grows proportionally (5:3 with right col)
                while keeping aspect; capped by parent's outer 7-col span. */}
            <div class="col-span-7 relative rounded-cvi-md overflow-hidden bg-cvi-bg-neutral-subtle aspect-[87/112] tablet-original:flex-[5] tablet-original:min-w-0">
                <CrossfadeImage
                    current={currentSlide()?.largerImage}
                    previous={previousSlide()?.largerImage}
                    srcsetWidths={LARGER_IMAGE_WIDTHS}
                    sizes={LARGER_IMAGE_SIZES}
                    fallbackWidth={1200}
                />
            </div>

            {/* Right column — 5 of 12 grid cols on mobile (with `self-end` to bottom-align with larger image);
                from tablet-original grows proportionally (3:5 with larger image) and `h-full justify-end` pushes
                content to the bottom of the row. */}
            <div class="col-span-5 self-end flex flex-col gap-cvi-5 tablet-original:self-auto tablet-original:h-full tablet-original:justify-end tablet-original:flex-[3] tablet-original:min-w-0">
                {/* Keyed Show — remount the caption on slide change so it fades in. */}
                <Show keyed when={currentSlide()}>
                    {(slide) => (
                        <Show when={slide.captionName || slide.captionRole}>
                            <div class="flex flex-col gap-cvi-2 animate-cvi-hero-slide-fade-in">
                                <Show when={slide.captionName}>
                                    <span class="body-sm font-cvi-medium text-cvi-fg-default">
                                        {slide.captionName}
                                    </span>
                                </Show>
                                <Show when={slide.captionRole}>
                                    <span class="body-sm font-cvi-regular text-cvi-fg-default">
                                        {slide.captionRole}
                                    </span>
                                </Show>
                            </div>
                        </Show>
                    )}
                </Show>

                <div class="relative rounded-cvi-md overflow-hidden bg-cvi-bg-neutral-subtle w-full aspect-[267/308]">
                    <CrossfadeImage
                        current={currentSlide()?.smallerImage}
                        previous={previousSlide()?.smallerImage}
                        srcsetWidths={SMALLER_IMAGE_WIDTHS}
                        sizes={SMALLER_IMAGE_SIZES}
                        fallbackWidth={750}
                    />
                </div>

                <Show when={isInteractive()}>
                    <div class="self-start flex items-center justify-center gap-cvi-3 pl-cvi-5 pr-cvi-2 py-cvi-2 rounded-cvi-round bg-cvi-bg-block-default">
                        <span class="body-xs font-cvi-regular text-cvi-fg-default whitespace-nowrap" aria-live="polite">
                            {currentIndex() + 1}/{slides().length}
                        </span>
                        <button
                            type="button"
                            onClick={togglePlay}
                            aria-label={
                                userPaused()
                                    ? localize('play-slideshow', 'Play slideshow')
                                    : localize('pause-slideshow', 'Pause slideshow')
                            }
                            class="size-cvi-9 relative rounded-cvi-round bg-transparent text-cvi-fg-default flex items-center justify-center cursor-pointer focus-ring-cvi-inset hover:bg-cvi-bg-menu-hover"
                        >
                            <ProgressArc
                                durationMs={AUTO_ADVANCE_MS}
                                resetKey={currentIndex()}
                                isPlaying={isPlaying}
                            />
                            <span class="relative">
                                <Show when={userPaused()} fallback={<PauseIcon size="small" ariaHidden />}>
                                    <PlayIcon size="small" ariaHidden />
                                </Show>
                            </span>
                        </button>
                    </div>
                </Show>
            </div>
        </div>
    );
};
